import React, { useState, useEffect } from 'react';
import { FiBox, FiPlusCircle, FiEdit2, FiTrash2, FiSearch, FiX, FiCheckCircle } from 'react-icons/fi';
import { inventoryService } from '../../services/api';
import { toast } from 'react-toastify';

const Inventory = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ 
    id: '', item_name: '', category: 'frame', quantity: 0, price: 0, 
    details: { demographic: 'unisex', material: 'plastic', metal_frame_type: 'full_frame' } 
  });

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const data = await inventoryService.getAll();
      setItems(data);
    } catch (e) {
      toast.error('Failed to fetch inventory');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.item_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || item.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const handleOpenModal = (item = null) => {
    if (item) {
      let parsedDetails = { demographic: 'unisex', material: 'plastic', metal_frame_type: 'full_frame' };
      if (item.details) {
        try { parsedDetails = typeof item.details === 'string' ? JSON.parse(item.details) : item.details; } catch(e){}
      }
      setFormData({ 
        id: item.id, 
        item_name: item.item_name, 
        category: item.category, 
        quantity: item.quantity, 
        price: item.price,
        details: parsedDetails
      });
    } else {
      setFormData({ 
        id: '', item_name: '', category: 'frame', quantity: 0, price: 0, 
        details: { demographic: 'unisex', material: 'plastic', metal_frame_type: 'full_frame' } 
      });
    }
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.item_name || formData.quantity === '' || formData.price === '') {
      toast.warning('Please fill all required fields');
      return;
    }
    
    try {
      await inventoryService.upsert({
        ...formData,
        quantity: parseInt(formData.quantity, 10),
        price: parseFloat(formData.price)
      });
      toast.success(formData.id ? 'Item updated successfully' : 'Item added successfully');
      setIsModalOpen(false);
      fetchInventory();
    } catch (e) {
      toast.error('Error saving item');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this specific item?')) {
      try {
        await inventoryService.delete(id);
        toast.success('Item deleted');
        fetchInventory();
      } catch (e) {
        toast.error('Error deleting item');
      }
    }
  };

  return (
    <div className="space-y-6 pb-10 animate-fade-in">
      {/* Header Area */}
      <div className="bg-white rounded-3xl p-8 shadow-xl shadow-slate-200/40 border border-slate-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-5">
           <FiBox size={100} />
        </div>
        <div className="relative z-10">
          <h2 className="text-3xl font-black text-slate-900 tracking-tight">Inventory Stock</h2>
          <p className="text-slate-500 font-medium mt-1">Manage frames, lenses, and accessories ready for dispensing.</p>
        </div>
        <button 
          onClick={() => handleOpenModal()}
          className="relative z-10 flex items-center space-x-2 px-6 py-3.5 bg-slate-900 text-white rounded-xl font-bold uppercase text-xs tracking-widest hover:bg-slate-800 hover:-translate-y-0.5 transition-all shadow-lg shadow-slate-200"
        >
          <FiPlusCircle size={16} />
          <span>Add New Stock</span>
        </button>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row justify-between gap-4 py-2">
        <div className="flex items-center space-x-2 bg-white p-2 rounded-xl border border-slate-200 shadow-sm w-max">
          {['all', 'frame', 'lens', 'accessory'].map(cat => (
            <button
              key={cat}
              onClick={() => setFilterCategory(cat)}
              className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-colors ${
                filterCategory === cat ? 'bg-syana-blue-50 text-syana-blue-600' : 'text-slate-400 hover:bg-slate-50'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
        <div className="relative w-full md:w-80">
          <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search by Item Name..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-semibold outline-none focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 shadow-sm transition-all"
          />
        </div>
      </div>

      {/* Table List */}
      <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/40 border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto no-scrollbar">
          <table className="w-full text-left text-sm whitespace-nowrap">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="px-6 py-5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Item Name</th>
                <th className="px-6 py-5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Category</th>
                <th className="px-6 py-5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Stock Units</th>
                <th className="px-6 py-5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Unit Price</th>
                <th className="px-6 py-5 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading ? (
                <tr><td colSpan="5" className="py-14 text-center font-bold text-slate-400">Loading stock data...</td></tr>
              ) : filteredItems.length === 0 ? (
                <tr><td colSpan="5" className="py-14 text-center font-bold text-slate-400">No specific items found in inventory.</td></tr>
              ) : filteredItems.map(item => (
                <tr key={item.id} className="hover:bg-slate-50/60 transition-colors group">
                  <td className="px-6 py-4 font-black text-sm text-slate-800">{item.item_name}</td>
                  <td className="px-6 py-4">
                    <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-[10px] font-black uppercase tracking-widest">
                      {item.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 font-black">
                     <span className={parseInt(item.quantity) <= 5 ? 'text-red-500' : 'text-green-600'}>
                       {item.quantity} Units
                     </span>
                  </td>
                  <td className="px-6 py-4 font-black text-slate-900">
                    Rs. {parseFloat(item.price).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-right space-x-3">
                    <button onClick={() => handleOpenModal(item)} className="p-2 text-slate-400 hover:text-syana-blue-600 hover:bg-syana-blue-50 rounded-lg transition-colors">
                      <FiEdit2 size={16} />
                    </button>
                    <button onClick={() => handleDelete(item.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                      <FiTrash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal / Form */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 min-h-screen">
          <div className="bg-white rounded-3xl overflow-hidden shadow-2xl relative w-full max-w-lg border border-slate-100" onClick={e => e.stopPropagation()}>
             <div className="p-6 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
               <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-xl bg-syana-blue-50 text-syana-blue-600 flex items-center justify-center">
                    <FiBox className="text-lg" />
                  </div>
                  <div>
                    <h3 className="font-black uppercase tracking-widest text-[10px] text-slate-400">Inventory Modal</h3>
                    <h4 className="font-bold text-sm text-slate-900">{formData.id ? 'Edit Item Stock' : 'Add New Item'}</h4>
                  </div>
               </div>
               <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-800 transition-colors p-2"><FiX size={20} /></button>
             </div>
             
             <form onSubmit={handleSave} className="p-6 space-y-6">
                <div className="space-y-4">
                  <div className="space-y-2 group">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest group-focus-within:text-syana-blue-600 transition-colors">Item Name & Details</label>
                    <input autoFocus required type="text" value={formData.item_name} onChange={e => setFormData({...formData, item_name: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:bg-white focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 transition-all shadow-sm" placeholder="e.g. RayBan Aviator Classic" />
                  </div>
                  
                  <div className="space-y-2 group">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest group-focus-within:text-syana-blue-600 transition-colors">Category</label>
                    <select value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:bg-white focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 transition-all shadow-sm cursor-pointer">
                      <option value="frame">Frame</option>
                      <option value="lens">Lens</option>
                      <option value="accessory">Accessory</option>
                    </select>
                  </div>

                  {formData.category === 'frame' && (
                    <div className="bg-syana-blue-50/50 p-4 rounded-xl border border-syana-blue-100 space-y-4">
                      <h4 className="text-[10px] font-black tracking-widest uppercase text-syana-blue-800 border-b border-syana-blue-200 pb-2">Frame Details</h4>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2 group">
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Demographic</label>
                          <select value={formData.details.demographic} onChange={e => setFormData({...formData, details: {...formData.details, demographic: e.target.value}})} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm font-semibold cursor-pointer outline-none focus:border-syana-blue-400 shadow-sm">
                            <option value="unisex">Unisex</option>
                            <option value="gents">Gents</option>
                            <option value="women">Women</option>
                            <option value="children">Children</option>
                          </select>
                        </div>
                        <div className="space-y-2 group">
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Material</label>
                          <select value={formData.details.material} onChange={e => setFormData({...formData, details: {...formData.details, material: e.target.value}})} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm font-semibold cursor-pointer outline-none focus:border-syana-blue-400 shadow-sm">
                            <option value="plastic">Plastic</option>
                            <option value="metal">Metal</option>
                          </select>
                        </div>
                      </div>

                      {formData.details.material === 'metal' && (
                        <div className="space-y-2 group animate-fade-in">
                          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest text-syana-blue-600">Metal Frame Type</label>
                          <select value={formData.details.metal_frame_type} onChange={e => setFormData({...formData, details: {...formData.details, metal_frame_type: e.target.value}})} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm font-bold text-syana-blue-900 cursor-pointer outline-none focus:border-syana-blue-400 shadow-inner">
                            <option value="full_frame">Full Frame</option>
                            <option value="half_rimless">Half Rimless</option>
                            <option value="full_rimless">Full Rimless</option>
                          </select>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2 group">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest group-focus-within:text-syana-blue-600 transition-colors">Qty in Stock</label>
                      <input required type="number" min="0" value={formData.quantity} onChange={e => setFormData({...formData, quantity: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-lg font-black text-slate-900 outline-none focus:bg-white focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 transition-all shadow-sm text-center" />
                    </div>
                    <div className="space-y-2 group">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest group-focus-within:text-syana-blue-600 transition-colors">Unit Price (Rs.)</label>
                      <input required type="number" step="0.01" min="0" value={formData.price} onChange={e => setFormData({...formData, price: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-lg font-black text-slate-900 outline-none focus:bg-white focus:border-syana-blue-400 focus:ring-4 focus:ring-syana-blue-50 transition-all shadow-sm text-center" />
                    </div>
                  </div>
                </div>
                
                <div className="pt-2">
                  <button type="submit" className="w-full py-4 bg-slate-900 text-white rounded-xl font-bold uppercase text-[11px] tracking-widest shadow-xl shadow-slate-200 hover:-translate-y-0.5 hover:bg-slate-800 transition-all active:scale-95 flex items-center justify-center space-x-2">
                    <FiCheckCircle />
                    <span>Save to Inventory</span>
                  </button>
                </div>
             </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
